import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Database, AlertCircle, Loader2, ChevronLeft, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const DATABASE_KEYS = [
  { key: 'core', label: 'Core Database', description: 'Vessel, users, and base data' },
  { key: 'operations', label: 'Operations Database', description: 'Port operations, voyages, and port inbox' },
  { key: 'voyage_estimate', label: 'Voyage Estimate Database', description: 'Voyage planning and estimates' },
  { key: 'reports', label: 'Reports Database', description: 'Report data and analytics' },
  { key: 'inquiry', label: 'Inquiry Database', description: 'Inquiry and quote management' }
];

export default function MultiDatabaseLogin() {
  const { connect } = useAuth();
  const [step, setStep] = useState(1); // 1: credentials, 2: map databases, 3: connecting
  const [formData, setFormData] = useState({
    host: '',
    port: '5432',
    user: '',
    password: ''
  });
  const [databases, setDatabases] = useState([]);
  const [databaseMapping, setDatabaseMapping] = useState({
    core: '',
    operations: '',
    voyage_estimate: '',
    reports: '',
    inquiry: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionResults, setConnectionResults] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
  };

  const handleListDatabases = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/list-databases', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          host: formData.host,
          port: formData.port,
          user: formData.user,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to list databases');
      }

      setDatabases(data.databases || []);
      setStep(2);

    } catch (err) {
      console.error('List databases error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDatabaseSelect = (key, dbName) => {
    setDatabaseMapping({
      ...databaseMapping,
      [key]: dbName
    });
  };

  const isFormValid = () => {
    // Check if all database keys are mapped
    return Object.values(databaseMapping).every(db => db !== '');
  };

  const handleConnect = async () => {
    if (!isFormValid()) {
      setError('Please select all 5 databases');
      return;
    }

    setLoading(true);
    setError(null);
    setStep(3);

    try {
      // Build secondary databases array (all except core which is primary)
      const secondaryDatabases = DATABASE_KEYS
        .filter(dbKey => dbKey.key !== 'core')
        .map(dbKey => ({
          name: databaseMapping[dbKey.key],
          key: dbKey.key
        }));

      const response = await fetch('/api/auth/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          host: formData.host,
          port: formData.port,
          database: databaseMapping.core, // Core is the primary database
          user: formData.user,
          password: formData.password,
          databases: secondaryDatabases
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Connection failed');
      }

      setConnectionResults(data.databases);

      // Store session token
      sessionStorage.setItem('sessionId', data.sessionId);
      sessionStorage.setItem('dbPassword', formData.password);
      const connInfo = {
        host: formData.host,
        port: formData.port,
        database: databaseMapping.core,
        user: formData.user,
        databases: data.databases,
        mapping: databaseMapping
      };
      sessionStorage.setItem('connectionInfo', JSON.stringify(connInfo));

      // Wait a moment to show results, then connect
      setTimeout(() => {
        connect(data.sessionId, connInfo);
      }, 2000);

    } catch (err) {
      console.error('Connection error:', err);
      setError(err.message);
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-2xl shadow-lg">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Database className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center">
            {step === 1 && 'Database Connection'}
            {step === 2 && 'Select Databases'}
            {step === 3 && 'Connecting to Databases'}
          </CardTitle>
          <CardDescription className="text-center">
            {step === 1 && 'Connect to your PostgreSQL database server'}
            {step === 2 && 'Map each application database to the correct server database'}
            {step === 3 && 'Establishing connections and loading table mappings'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 && (
            // Step 1: Enter credentials
            <form onSubmit={handleListDatabases} className="space-y-4">
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">{error}</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="host">Database Host / IP Address</Label>
                <Input
                  id="host"
                  name="host"
                  type="text"
                  placeholder="localhost or 192.168.1.100"
                  value={formData.host}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="port">Port</Label>
                <Input
                  id="port"
                  name="port"
                  type="number"
                  placeholder="5432"
                  value={formData.port}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="user">Username</Label>
                <Input
                  id="user"
                  name="user"
                  type="text"
                  placeholder="postgres"
                  value={formData.user}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading Databases...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    List Databases
                  </>
                )}
              </Button>
            </form>
          )}

          {step === 2 && (
            // Step 2: Map databases
            <div className="space-y-6">
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">{error}</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Map Server Databases</Label>
                <p className="text-xs text-gray-500 mb-4">
                  Select which database on the server corresponds to each application database
                </p>

                <div className="space-y-4">
                  {DATABASE_KEYS.map((dbConfig) => (
                    <div key={dbConfig.key} className="space-y-2">
                      <Label htmlFor={`db-${dbConfig.key}`} className="text-sm font-semibold">
                        {dbConfig.label}
                      </Label>
                      <p className="text-xs text-gray-500 mb-2">
                        {dbConfig.description}
                      </p>
                      <Select
                        value={databaseMapping[dbConfig.key]}
                        onValueChange={(value) => handleDatabaseSelect(dbConfig.key, value)}
                      >
                        <SelectTrigger id={`db-${dbConfig.key}`} className="w-full">
                          <SelectValue placeholder="Select database..." />
                        </SelectTrigger>
                        <SelectContent>
                          {databases.map((db) => (
                            <SelectItem key={db.name} value={db.name}>
                              <div className="flex items-center justify-between w-full">
                                <span>{db.name}</span>
                                {db.size && (
                                  <span className="text-xs text-gray-500 ml-2">
                                    {db.size}
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-800 font-medium">
                    ✓ Mapped: {Object.values(databaseMapping).filter(v => v).length} / 5 databases
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setStep(1);
                    setDatabaseMapping({
                      core: '',
                      operations: '',
                      voyage_estimate: '',
                      reports: '',
                      inquiry: ''
                    });
                    setDatabases([]);
                    setError(null);
                  }}
                  disabled={loading}
                  className="gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back to Credentials
                </Button>
                <Button
                  type="button"
                  onClick={handleConnect}
                  disabled={loading || !isFormValid()}
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Connect to 5 Databases
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            // Step 3: Connection results
            <div className="space-y-4">
              {connectionResults && (
                <div className="space-y-3">
                  {/* Primary Database */}
                  <div className="p-4 border rounded-lg bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Database className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold">Primary Database</span>
                      </div>
                      {connectionResults.primary.status === 'connected' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div className="ml-7 space-y-1">
                      <div className="text-sm font-medium text-gray-900">
                        {connectionResults.primary.database}
                      </div>
                      <div className="text-xs text-gray-500">
                        {connectionResults.primary.tables} tables loaded
                      </div>
                    </div>
                  </div>

                  {/* Secondary Databases */}
                  {Object.entries(connectionResults.secondary || {}).map(([key, result]) => (
                    <div key={key} className="p-4 border rounded-lg bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Database className="w-5 h-5 text-gray-600" />
                          <span className="font-semibold capitalize">{key}</span>
                        </div>
                        {result.status === 'connected' ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div className="ml-7 space-y-1">
                        <div className="text-sm font-medium text-gray-900">
                          {result.database}
                        </div>
                        {result.status === 'connected' ? (
                          <div className="text-xs text-gray-500">
                            {result.tables} tables loaded
                          </div>
                        ) : (
                          <div className="text-xs text-red-600">
                            {result.error}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
              <p className="text-center text-sm text-gray-600">
                Finalizing connections...
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
